import {syntaxTree} from '@codemirror/language';
import type {
  CompletionContext,
  CompletionResult,
} from '@codemirror/autocomplete';
import type {CompletionData} from './types';

/**
 * Walks up from a syntax tree node to find an ancestor with the given name.
 */
function findAncestor(
  node: {name: string; parent: {name: string; parent: any} | null} | null,
  name: string
) {
  let current = node;
  while (current) {
    if (current.name === name) return current;
    current = current.parent;
  }
  return null;
}

/**
 * Returns the attribute name for the given position by looking at the syntax tree.
 */
function getAttributeName(
  state: {doc: {sliceString: (from: number, to: number) => string}},
  tree: ReturnType<typeof syntaxTree>,
  pos: number
): string | null {
  const node = tree.resolveInner(pos, -1);
  const attr = findAncestor(node as any, 'Attribute');
  if (!attr) return null;
  const nameNode = (attr as any).getChild?.('AttributeName');
  if (nameNode) {
    return state.doc.sliceString(nameNode.from, nameNode.to);
  }
  return null;
}

/**
 * Completion source for HTML contexts.
 * - Inside `class="..."` → suggest CSS class names
 */
export function htmlCompletionSource(
  context: CompletionContext,
  data: CompletionData
): CompletionResult | null {
  const {state, pos} = context;
  const tree = syntaxTree(state);
  const node = tree.resolveInner(pos, -1);

  const isInAttributeValue =
    node.name === 'AttributeValue' ||
    findAncestor(node as any, 'AttributeValue') != null;

  if (isInAttributeValue) {
    const attrName = getAttributeName(state, tree, pos);

    if (attrName === 'class') {
      return getClassCompletions(context, data);
    }

    return null;
  }

  // Fallback: detect class="..." by raw text when the syntax tree is incomplete
  // (e.g., cursor inside an unclosed attribute or parser hasn't caught up).
  return getClassCompletionsByText(context, data);
}

function getClassCompletions(
  context: CompletionContext,
  data: CompletionData
): CompletionResult | null {
  const word = context.matchBefore(/[\w-]*/);
  if (!word) return null;

  const options = data.cssClasses.map((cls) => ({
    label: cls.className,
    displayLabel:
      cls.displayName !== cls.className ? cls.displayName : undefined,
    type: 'class' as const,
  }));

  if (options.length === 0) return null;

  return {
    from: word.from,
    options,
    validFor: /^[\w-]*$/,
  };
}

/**
 * Text-based fallback for class attribute completions. Detects `class="..."` by
 * scanning the text before the cursor rather than relying on the syntax tree.
 * This handles cases where the parser is incomplete (e.g., unclosed attributes)
 * or the cursor is inside a `<style>` block where HTML nodes look like CSS to
 * the parser.
 */
export function getClassCompletionsByText(
  context: CompletionContext,
  data: CompletionData
): CompletionResult | null {
  if (data.cssClasses.length === 0) return null;

  const {pos} = context;
  const line = context.state.doc.lineAt(pos);
  const textBefore = line.text.slice(0, pos - line.from);

  // Match class="..." or class='...' with cursor inside the quotes
  const match = textBefore.match(/\bclass\s*=\s*["']([^"']*)$/);
  if (!match) return null;

  // Support space-separated classes: complete only the last word
  const typed = match[1] ?? '';
  const lastSpace = typed.lastIndexOf(' ');
  const prefix = lastSpace >= 0 ? typed.slice(lastSpace + 1) : typed;
  const from = pos - prefix.length;

  const options = data.cssClasses.map((cls) => ({
    label: cls.className,
    displayLabel:
      cls.displayName !== cls.className ? cls.displayName : undefined,
    type: 'class' as const,
  }));

  return {
    from,
    options,
    validFor: /^[\w-]*$/,
  };
}
